import {Video} from './video';
import {TitleImage} from './title-image';
import {Episode} from './episode';
import {Season} from './season';
import {Review} from './review';
import {Genre} from '@app/titles/models/genre';
import {Keyword} from '@app/titles/models/keyword';
import {ProductionCountry} from '@app/titles/models/production-country';
import {Person} from '@app/titles/models/person';
import {NewsArticle} from '@app/titles/models/news-article';

export interface EpisodeCredit extends Episode {
  pivot: TitleCreditPivot;
}

export interface TitleCredit extends Person {
  pivot: TitleCreditPivot;
}

export interface PersonCredit extends Title {
  credited_episode_count?: number;
  episodes: EpisodeCredit[];
  pivot: TitleCreditPivot;
}

export interface TitleCreditPivot {
  id: number;
  job: string;
  department: 'directing' | 'writing' | 'actors' | 'creators';
  character: string;
}

export const TITLE_MODEL = 'title';
export const MOVIE_MODEL = 'movie';
export const SERIES_MODEL = 'series';

/**
 * Day 13 — AI-generated Turkish content (FilmIcerik).
 * Backend (Title::toArray) sadece durum='done' olunca doldurur, aksi
 * halde aiContent=null gelir. Internal field'lar (durum, hata_mesaji,
 * kalite_skoru, ai_provider) API yuzeyine sizmaz.
 *
 * B26 (Day 35 / Plan v9.2) — 5 dil mimari. Backend FilmIcerik->
 * localizedPayload() aktif locale için yeni alanları doldurur:
 *   - summary           (aktif locale ozet, fallback ozet_tr)
 *   - why_watch         (aktif locale neden_izlemeli, fallback TR)
 *   - interesting_facts (aktif locale bilinmeyenler, fallback TR)
 *   - localized_faq     (aktif locale faq, fallback TR)
 * Eski TR alanları (ozet_tr, neden_izlemeli, bilinmeyenler, faq)
 * geriye uyumluluk için korunur — cache/SSR fallback'i bozmasın.
 */
export interface TitleAiContent {
  ozet_tr: string;
  neden_izlemeli: string;
  bilinmeyenler: Array<{title: string; content: string}>;
  faq: Array<{q: string; a: string}>;
  guncellenme: string;
  /** B26 — aktif locale için lokalize özet (translation yoksa TR fallback). */
  summary?: string | null;
  /** B26 — aktif locale için "neden izlemeli" (TR fallback). */
  why_watch?: string | null;
  /** B26 — aktif locale için ilginç bilgiler (TR fallback). */
  interesting_facts?: Array<{title: string; content: string}>;
  /** B26 — aktif locale için FAQ (TR fallback). */
  localized_faq?: Array<{q: string; a: string}>;
}

export interface Title {
  id: number;
  slug?: string;
  /** R48 (Day 32) — non-TR locale URL'lerinde kullanılan İngilizce slug. */
  slug_en?: string;
  name: string;
  original_title: string;
  /** R5 (5 Tem) — aktif locale'in dağıtım adı (title_translations.name;
   * TitleLoader post-cache ekler). Görünen ad önceliği:
   * localized_name > original_title > name. */
  localized_name?: string;
  model_type: typeof TITLE_MODEL;
  is_series: boolean;
  status: 'released' | 'upcoming' | 'ongoing' | 'ended';
  description: string;
  tagline: string;
  runtime: number;
  rating: number;
  /** PUAN OVERHAUL (5 Tem) — ham puan kolonları artık payload'da açık.
   * Birincil gösterim IMDb (varsa) → TMDB fallback; Cine5 Puanı yalnız
   * local_vote_count > 0 iken gösterilir (bkz. titles/primary-rating.ts). */
  tmdb_vote_average?: number | null;
  tmdb_vote_count?: number | null;
  local_vote_average?: number | null;
  local_vote_count?: number | null;
  imdb_id?: string | null;
  imdb_rating?: number | null;
  imdb_votes?: number | null;
  rt_score?: number | null;
  metacritic_score?: number | null;
  budget: number;
  poster?: string;
  backdrop: string;
  revenue: number;
  views: number;
  popularity: number;
  seasons_count: number;
  release_date: string;
  year: number;
  genres: Genre[];
  keywords: Keyword[];
  production_countries: ProductionCountry[];
  videos: Video[];
  all_videos?: Video[];
  primary_video: Video;
  primary_video_count?: number;
  certification?: string;
  images: TitleImage[];
  season?: Season;
  seasons?: Season[];
  reviews?: Review[];
  language: string;
  updated_at?: string;
  watch_providers_tr?: WatchProvider[];
  watchProviders?: WatchProvidersPayload | null;
  aiContent?: TitleAiContent | null;
  /** D39 — Dizi Detay "Haberler" sekmesi: bağlı haberler (sadece dizi +
   *  eager-load yapıldığında dolar; boşsa frontend sekmeyi gizler). */
  newsArticles?: NewsArticle[];
}

/**
 * Day 19 — Affiliate "Nerede İzlenir" payload.
 *
 * Backend (Title::toArray) sadece relationLoaded('watchProviders') ise
 * doldurur. deepLink AffiliateLinkBuilder ile UTM (+ JustWatch aid)
 * wrap'lidir.
 */
export interface WatchProviderItem {
  id: number;
  name: string;
  logo: string;
  /** Marka rengi (#hex) — logo yoksa dürüst rozet arkaplanı için. */
  color?: string;
  offerType: 'flatrate' | 'rent' | 'buy' | 'ads' | 'free';
  price: number | null;
  currency: string | null;
  deepLink: string;
  orderPriority: number;
}

export interface WatchProvidersPayload {
  flatrate: WatchProviderItem[];
  rent: WatchProviderItem[];
  buy: WatchProviderItem[];
  ads: WatchProviderItem[];
  free: WatchProviderItem[];
  region: string;
}

export interface WatchProvider {
  name: string;
  logo: string;
  provider_id: number;
  /** WatchMode types: sub, free, tve, rent, buy. TMDB compat: flatrate=sub */
  type: 'sub' | 'flatrate' | 'free' | 'tve' | 'rent' | 'buy';
  type_label: string;
  cta?: string;
  priority?: number;
  link: string | null;
  justwatch_link?: string | null;
  price?: string | null;
  format?: string;
  brand_color?: string;
}
